// DelenieDokladu — rozdelenie došlého dokladu na časti (Mirko 17. 9. 2026: „delenie faktúr robíš zložito, je to jednoduché“).
// p44 JEDNODUCHÉ DELENIE: doklad má ČASŤ 1 a ČASŤ 2 (podľa potreby pribudne ďalšia). Pri každej ČASTI sa zaradenie vyberie RAZ
// (Evidenčná / Réžia + kategória / Zákazka + zákazka a kategória). Položky sa len klikom presúvajú medzi časťami — „položka v riadku tu,
// položka tam“. Suma časti je súčet jej položiek bez DPH tak, ako sú na faktúre; sumu nezadáva nikto ručne (server ju prepočíta).
// Doklad bez položiek sa nedelí. Server: GET/PUT/DELETE /api/incoming-invoices/:id/casti.
import React, { useState, useEffect } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'
import { BtnAdd, BtnGhost } from './Btn.jsx'
import { SearchableSelect } from './Ui.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK } from '../theme.js'

const TYPY = [{ key: 'evidencna', label: 'Evidenčná', color: '#6366f1' }, { key: 'rezia', label: 'Réžia', color: '#7c3aed' }, { key: 'zakazka', label: 'Zákazka', color: '#0ea5e9' }]
const FARBY_CASTI = ['#0ea5e9', '#7c3aed', '#0f766e', '#b45309', '#be185d']
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100
const chybaCasti = (c) => !c.typ ? 'typ' : (c.typ !== 'evidencna' && !String(c.kategoria || '').trim()) ? 'kategória' : (c.typ === 'zakazka' && !c.projekt) ? 'zákazka' : ''
const pocetPoloziek = (n) => n + (n === 1 ? ' položka' : n < 5 ? ' položky' : ' položiek')

export default function DelenieDokladu({ invoiceId, projects = [], reziaCats = [], zakazkaCats = [], onSaved, onCancel }) {
  const toast = useToast()
  const [data, setData] = useState(null)
  const [casti, setCasti] = useState([{ typ: 'evidencna', kategoria: '', projekt: '' }, { typ: 'zakazka', kategoria: '', projekt: '' }])
  const [kam, setKam] = useState({})            // id položky → poradie časti (0, 1, 2…)
  const [oznacene, setOznacene] = useState([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let zive = true
    fetchAuth(`/api/incoming-invoices/${encodeURIComponent(invoiceId)}/casti`).then(r => r.ok ? r.json() : null).then(d => {
      if (!zive || !d) return
      const ulozene = (d.casti || []).map(c => ({
        typ: c.invoice_kind === 'evidencna' ? 'evidencna' : (c.cost_center || 'rezia'),
        kategoria: c.cost_category || '', projekt: c.project_id || '',
      }))
      const mapa = {}
      ;(d.casti || []).forEach((c, i) => { for (const x of (c.polozky || [])) mapa[String(x && typeof x === 'object' ? x.id : x)] = i })
      if (ulozene.length >= 2) { setCasti(ulozene); setKam(mapa) }
      else setKam(Object.fromEntries((d.polozky || []).map(p => [String(p.id), 0])))
      setData({ ...d, polozky: d.polozky || [] })
    }).catch(() => toast.error('Načítanie dokladu zlyhalo'))
    return () => { zive = false }
  }, [invoiceId])

  const zrus = async () => {
    if (!window.confirm('Zrušiť delenie? Doklad sa vráti medzi doklady na určenie a zaradíš ho znova.')) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/incoming-invoices/${encodeURIComponent(invoiceId)}/casti`, { method: 'DELETE' })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(d.error || 'Zrušenie zlyhalo'); return }
      toast.success('Delenie zrušené — doklad čaká na určenie')
      onSaved && onSaved()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  if (!data) return <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, padding: SPACE.sm }}>Načítavam doklad…</div>

  const polozky = data.polozky
  if (!polozky.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.warn, background: 'rgba(217,119,6,0.08)', borderRadius: RADIUS.sm, padding: '8px 12px', lineHeight: 1.5 }}>
          <b>Doklad nemá položky.</b> Delí sa po položkách — položka ide do jednej časti celá a suma je vždy suma položky z faktúry.
          Najprv doplň položky faktúry v záložke <b>Položky</b>, potom doklad rozdeľ.
        </div>
        <div style={{ display: 'flex', gap: SPACE.sm }}>
          {data.delena && <BtnGhost onClick={zrus} disabled={busy}>Zrušiť delenie</BtnGhost>}
          {onCancel && <BtnGhost onClick={onCancel} disabled={busy}>Späť</BtnGhost>}
        </div>
      </div>
    )
  }

  const idOf = (p) => String(p.id)
  const castOf = (id) => Math.min(kam[id] == null ? 0 : kam[id], casti.length - 1)
  const presun = (ids, i) => setKam(prev => { const n = { ...prev }; for (const id of ids) n[id] = i; return n })
  const zmenCast = (i, zmena) => setCasti(prev => prev.map((c, x) => x !== i ? c : { ...c, ...zmena }))
  const pridajCast = () => setCasti(prev => prev.length >= 5 ? prev : [...prev, { typ: 'rezia', kategoria: '', projekt: '' }])
  const zmazCast = (i) => {
    if (casti.length <= 2) return
    setKam(prev => Object.fromEntries(Object.entries(prev).map(([id, x]) => [id, x === i ? 0 : x > i ? x - 1 : x])))
    setCasti(prev => prev.filter((_, x) => x !== i))
  }
  const prepniOznacenie = (id) => setOznacene(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const vsetkyOznacene = oznacene.length === polozky.length

  const suma = (i) => r2(polozky.filter(p => castOf(idOf(p)) === i).reduce((s, p) => s + (Number(p.bez_dph) || 0), 0))
  const pocet = (i) => polozky.filter(p => castOf(idOf(p)) === i).length
  const sumaDokladu = Number(data.suma_bez_dph) || 0
  const sucetPoloziek = r2(polozky.reduce((s, p) => s + (Number(p.bez_dph) || 0), 0))
  const nesediZaklad = Math.abs(sucetPoloziek - sumaDokladu) > 0.01
  const plne = casti.map((c, i) => ({ c, i, n: pocet(i) })).filter(x => x.n > 0)
  const chybne = plne.filter(x => chybaCasti(x.c))
  const pripravene = !nesediZaklad && plne.length >= 2 && !chybne.length
  const dovod = nesediZaklad ? 'Súčet položiek ' + SK.eur(sucetPoloziek) + ' nesedí so základom dokladu ' + SK.eur(sumaDokladu) + ' — oprav položky alebo základ v detaile dokladu.'
    : plne.length < 2 ? 'Všetky položky sú v jednej časti — doklad nedeľ, zaraď ho celý, alebo presuň časť položiek.'
      : chybne.length ? 'Doplň zaradenie pri ČASTI ' + chybne.map(x => x.i + 1).join(' a ') + ' (chýba ' + chybne.map(x => chybaCasti(x.c)).join(', ') + ').' : ''

  const uloz = async () => {
    setBusy(true)
    try {
      const telo = {
        casti: plne.map(({ c, i }) => ({
          invoice_kind: c.typ === 'evidencna' ? 'evidencna' : 'nakladova',
          cost_center: c.typ === 'evidencna' ? '' : c.typ,
          cost_category: c.typ === 'evidencna' ? '' : c.kategoria,
          project_id: c.typ === 'zakazka' ? c.projekt : '',
          suma: suma(i),
          polozky: polozky.filter(p => castOf(idOf(p)) === i).map(idOf),
        })),
      }
      const r = await fetchAuth(`/api/incoming-invoices/${encodeURIComponent(invoiceId)}/casti`, { method: 'PUT', body: JSON.stringify(telo) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(d.error || 'Delenie sa neuložilo'); return }
      toast.success('Doklad rozdelený na ' + plne.length + ' časti')
      onSaved && onSaved()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  const farba = (i) => FARBY_CASTI[i % FARBY_CASTI.length]
  const tlacidloCasti = (i, aktivna, onClick, mala) => (
    <button key={i} type="button" onClick={onClick}
      style={{ padding: mala ? '2px 9px' : '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: 700, color: aktivna ? '#fff' : farba(i), background: aktivna ? farba(i) : 'transparent', border: `2px solid ${farba(i)}`, borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
      ČASŤ {i + 1}</button>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, lineHeight: 1.45 }}>
        Doklad sa delí na časti: pri <b>každej časti raz vyber, kam patrí</b>, a potom klikom pošli položky do tej časti.
        Sumy sa zrátajú samy — cena je vždy cena položky z faktúry ({data.polozky_s_dph ? 's DPH, prepočítaná bez DPH' : 'bez DPH'}).
      </div>
      {data.zaklad_vyplneny === false && (
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.warn, background: 'rgba(217,119,6,0.08)', borderRadius: RADIUS.sm, padding: '6px 10px', lineHeight: 1.45 }}>
          Doklad nemá vyplnený základ bez DPH — Matrix berie súčet položiek <b>{SK.eur(sumaDokladu)}</b>. Ak nesedí s faktúrou, oprav základ a DPH v detaile dokladu.
        </div>
      )}

      {/* ČASTI — zaradenie sa vyberie raz pri časti */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
        {casti.map((c, i) => {
          const ch = pocet(i) > 0 ? chybaCasti(c) : ''
          return (
            <div key={i} style={{ border: `1px solid ${ch ? COLORS.warn : COLORS.line}`, borderLeft: `4px solid ${farba(i)}`, borderRadius: RADIUS.md, padding: SPACE.sm, background: COLORS.bg, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
                <b style={{ color: farba(i) }}>ČASŤ {i + 1}</b>
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{pocetPoloziek(pocet(i))}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>{SK.eur(suma(i))}</span>
                {casti.length > 2 && pocet(i) === 0 && <BtnGhost size="sm" onClick={() => zmazCast(i)}>Zrušiť časť</BtnGhost>}
              </div>
              <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  {TYPY.map(t => (
                    <button key={t.key} type="button" onClick={() => zmenCast(i, { typ: t.key, kategoria: '', projekt: t.key === 'zakazka' ? c.projekt : '' })}
                      style={{ padding: '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: 700, color: c.typ === t.key ? '#fff' : t.color, background: c.typ === t.key ? t.color : 'transparent', border: `2px solid ${t.color}`, borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit' }}>{t.label}</button>
                  ))}
                </span>
                {c.typ === 'zakazka' && (
                  <div style={{ minWidth: 240 }}>
                    <SearchableSelect menuPortal value={c.projekt} onChange={v => zmenCast(i, { projekt: v })} error={ch === 'zákazka'}
                      options={projects.map(p => ({ value: p.id, label: `${p.name}${p.zakazka_code ? ' · ' + p.zakazka_code : ''}` }))} placeholder="Zákazka *" />
                  </div>
                )}
                {c.typ !== 'evidencna' && (
                  <div style={{ minWidth: 220 }}>
                    <SearchableSelect menuPortal value={c.kategoria} onChange={v => zmenCast(i, { kategoria: v })} allowCreate error={ch === 'kategória'}
                      options={(c.typ === 'zakazka' ? zakazkaCats : reziaCats).map(k => ({ value: k, label: k }))} placeholder="Kategória nákladu *" />
                  </div>
                )}
                {c.typ === 'evidencna' && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>materiál na sklad — bez kategórie, nie je náklad</span>}
              </div>
            </div>
          )
        })}
        {casti.length < 5 && <div><BtnGhost size="sm" onClick={pridajCast}>+ Pridať časť</BtnGhost></div>}
      </div>

      {/* POLOŽKY — klikom idú do časti */}
      <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: COLORS.bgSoft, fontSize: TYPO.sizes.xs, color: COLORS.muted, borderBottom: `1px solid ${COLORS.line}`, flexWrap: 'wrap' }}>
          <input type="checkbox" checked={vsetkyOznacene} onChange={() => setOznacene(vsetkyOznacene ? [] : polozky.map(idOf))} aria-label="Označiť všetky položky" />
          <span style={{ flex: 1 }}>Položka dokladu</span>
          {oznacene.length > 0
            ? <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: COLORS.text }}>Označené ({oznacene.length}) dať do:</span>
                {casti.map((c, i) => tlacidloCasti(i, false, () => { presun(oznacene, i); setOznacene([]) }, true))}
              </span>
            : <span>Suma bez DPH</span>}
        </div>
        {polozky.map((p, idx) => {
          const id = idOf(p), i = castOf(id)
          return (
            <div key={id} style={{ padding: '6px 10px', borderTop: idx ? `1px solid ${COLORS.lineSoft}` : 'none', background: COLORS.bg, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input type="checkbox" checked={oznacene.includes(id)} onChange={() => prepniOznacenie(id)} aria-label={'Označiť ' + (p.name || '')} />
              <span style={{ flex: 1, minWidth: 140, fontSize: TYPO.sizes.sm }}>{p.name}{p.qty ? <span style={{ color: COLORS.muted }}> · {p.qty} {p.unit || ''}</span> : null}</span>
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {casti.map((c, x) => tlacidloCasti(x, x === i, () => presun([id], x), true))}
              </span>
              <span style={{ fontFamily: TYPO.mono, whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>{SK.eur(p.bez_dph)}</span>
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: TYPO.sizes.sm, color: COLORS.text }}>Doklad bez DPH <b>{SK.eur(sumaDokladu)}</b> · v častiach <b>{SK.eur(r2(plne.reduce((s, x) => s + suma(x.i), 0)))}</b></span>
      </div>
      <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <BtnAdd onClick={uloz} disabled={busy || !pripravene}>{busy ? 'Ukladám…' : 'Uložiť delenie'}</BtnAdd>
        {data.delena && <BtnGhost onClick={zrus} disabled={busy}>Zrušiť delenie</BtnGhost>}
        {onCancel && <BtnGhost onClick={onCancel} disabled={busy}>Späť</BtnGhost>}
        {!pripravene && dovod && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, alignSelf: 'center' }}>{dovod}</span>}
      </div>
    </div>
  )
}
